import { useId } from 'react'
import type { ChangeEvent } from 'react'
import {
  CloseOutlined,
  DownloadOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '../../../components/icons'
import { TECHNICAL_DRAWING_ACCEPT } from './types'
import type { PartAttachmentFileKind } from './types'
import { defaultPartAttachmentsService, usePartAttachments } from './usePartAttachments'
import type { PartAttachmentsService } from './usePartAttachments'

export interface PartAttachmentsSectionProps {
  readonly partId: string
  /** Defaults to `'technical_drawing'` — the only reusable control this
   * story ships (AC1). A future embed (Story C3's config modal) can pass a
   * different kind without this component changing shape. */
  readonly fileKind?: PartAttachmentFileKind
  /** Injectable for tests / a future BFF-backed swap — matches every other
   * vertical's service-injection seam in this codebase. */
  readonly service?: PartAttachmentsService
  /** Omits this component's own `<h3>` heading so a caller that already
   * renders its own section header (e.g. Story C3's config-modal accordion
   * row) can embed just the control body. Defaults to showing it, since
   * every other consumer today (the part-preview surface) needs one. */
  readonly hideHeading?: boolean
  /** UID-235 (B2) — the Part detail page's CUSTOM view embeds this SAME
   * component read-only-first (spec §3): show the persisted attachment
   * list + download, but omit the upload control/pro-tip (the modal's
   * upload affordance stays the only write path for v1). Defaults to
   * `false` so every existing call site (the config modal's Drawing
   * analysis tab, the preview drawer) is unaffected. */
  readonly readOnly?: boolean
}

const FILE_KIND_COPY: Record<
  PartAttachmentFileKind,
  { readonly heading: string; readonly cta: string }
> = {
  technical_drawing: { heading: 'Technical drawing', cta: 'Upload drawing' },
  model: { heading: '3D model', cta: 'Upload model' },
  specification: { heading: 'Specification', cta: 'Upload file' },
  other: { heading: 'File', cta: 'Upload file' },
}

/**
 * The reusable "Technical drawing (optional)" attachment control (UID-155,
 * B3 — Parts Library epic UID-3, wave 2, `Fictiv Platform.dc.html` ~L4976-
 * 4998). Presign→PUT→persist against the real, KEYLESS B0/B2 contract
 * (`usePartAttachments`) — this component never sees, stores, or renders an
 * `object_key`; the contract doesn't have one (SECURITY INVARIANT, see
 * `./types.ts`).
 *
 * Built standalone (no config-modal chrome, no revision-modal chrome) but
 * as a single self-contained, service-injectable component precisely so
 * Story C3 can mount this SAME component inside the config modal's
 * "Technical drawing (optional)" accordion row later — `hideHeading` is the
 * only seam C3 is expected to need; everything else (list/upload/download/
 * validation/aria-live) is already the full behavior either surface wants.
 *
 * WCAG: the file input has a real `<label>` (visible programmatic label,
 * not just a placeholder, satisfying "file input has a visible programmatic
 * label"); the upload label and each attachment's download/remove
 * affordances are explicitly sized to the ≥44×44px target this story
 * requires (`.part-attachments-upload-label`/`.part-attachments-chip-action`
 * in `parts.css` — stricter than the shared `.icon-btn` primitive's 24px
 * WCAG-AA-minimum default, set locally rather than by bumping that token
 * repo-wide); every validation/server error is TEXT tied to the control via
 * `aria-describedby` (3.3.1) — never color-only; upload progress/completion
 * is announced via a visually-hidden `aria-live="polite"` region (AC5).
 */
export function PartAttachmentsSection({
  partId,
  fileKind = 'technical_drawing',
  service = defaultPartAttachmentsService,
  hideHeading = false,
  readOnly = false,
}: PartAttachmentsSectionProps) {
  const { state, pending, formError, announcement, uploadFile, clearPending, downloadAttachment } =
    usePartAttachments(partId, service)
  const inputId = useId()
  const errorId = useId()
  const copy = FILE_KIND_COPY[fileKind]
  const isPdf = fileKind === 'technical_drawing'

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    void uploadFile(file, fileKind)
  }

  const attachments = state.status === 'ready' ? state.attachments : []
  const listError = state.status === 'error' ? state.message : null

  return (
    <section
      className="part-attachments-section"
      aria-labelledby={hideHeading ? undefined : `${inputId}-heading`}
    >
      {!hideHeading && (
        <h3 id={`${inputId}-heading`} className="part-attachments-heading">
          {copy.heading} <span className="part-attachments-heading-optional">(optional)</span>
        </h3>
      )}

      {attachments.length > 0 && (
        <ul
          className="part-attachments-list"
          aria-label={`Uploaded ${copy.heading.toLowerCase()} files`}
        >
          {attachments.map((attachment) => (
            <li key={attachment.id} className="part-attachments-chip part-attachments-chip-filled">
              <span className="part-attachments-chip-icon" aria-hidden="true">
                <FileTextOutlined />
              </span>
              <span className="part-attachments-chip-body">
                <span className="part-attachments-chip-label">{copy.heading}</span>
                <span className="part-attachments-chip-filename">{attachment.filename}</span>
              </span>
              <button
                type="button"
                className="icon-btn part-attachments-chip-action"
                onClick={() => downloadAttachment(attachment.id)}
                aria-label={`Download ${attachment.filename}`}
              >
                <DownloadOutlined aria-hidden="true" />
              </button>
              {/* No delete-attachment endpoint exists in B0 (architect note,
                  UID-152) — a persisted attachment is honestly NOT removable
                  from this UI; no dead "remove" control is rendered for it. */}
            </li>
          ))}
        </ul>
      )}

      {pending.map((item) => (
        <div key={item.localId} className="part-attachments-chip part-attachments-chip-pending">
          <span className="part-attachments-chip-icon" aria-hidden="true">
            <FileTextOutlined />
          </span>
          <span className="part-attachments-chip-body">
            <span className="part-attachments-chip-filename">{item.filename}</span>
            <span className="caption-text">
              {item.status === 'uploading' && `Uploading… ${item.progress}%`}
              {item.status === 'persisting' && 'Saving…'}
              {item.status === 'error' && (item.error ?? 'Upload failed')}
            </span>
          </span>
          {item.status === 'error' && (
            <button
              type="button"
              className="icon-btn part-attachments-chip-action"
              onClick={() => clearPending(item.localId)}
              aria-label={`Remove ${item.filename}`}
            >
              <CloseOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      ))}

      {!readOnly && (
        <div className="part-attachments-actions">
          <label htmlFor={inputId} className="btn btn-outline btn-md part-attachments-upload-label">
            <UploadOutlined aria-hidden="true" /> {copy.cta}
          </label>
          <input
            id={inputId}
            type="file"
            accept={isPdf ? TECHNICAL_DRAWING_ACCEPT : undefined}
            className="sr-only"
            onChange={handleFileChange}
            aria-describedby={formError || listError ? errorId : undefined}
          />
        </div>
      )}

      {!readOnly && isPdf && (
        <p className="caption-text part-attachments-support-note">Supported file format: .pdf</p>
      )}

      {(formError || listError) && (
        <p id={errorId} role="alert" className="part-attachments-error">
          {formError ?? listError}
        </p>
      )}

      {!readOnly && isPdf && (
        <div className="part-attachments-pro-tip">
          <span className="part-attachments-pro-tip-icon" aria-hidden="true">
            i
          </span>
          <p>
            <strong>Pro tip!</strong> Include technical callouts in your drawing such as process,
            material, finish, masking, and certificates to automatically configure your part.
          </p>
        </div>
      )}

      {readOnly && attachments.length === 0 && (
        <p className="caption-text">No technical drawings on file for this part yet.</p>
      )}

      {/* AC5: upload progress/completion announced via aria-live without
          duplicating the visible chip/error text above. */}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </section>
  )
}
